import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useManagerTodayData } from '../../../hooks/useManagerTodayData';
import Sidebar from '../../admin-dashboard/components/Sidebar';
import CockpitBar from '../../../components/manager-v2/CockpitBar';
import HeroProjection from '../../../components/manager-v2/HeroProjection';
import EtatBlock from '../../../components/manager-v2/EtatBlock';
import TopActionsSection from '../../../components/manager-v2/TopActionsSection';

/**
 * Manager V2 Today — Page d'accueil manager (Sprint 2).
 *
 * Modules :
 *  - CockpitBar         : strip statut "OPS LIVE · TEAM N/M · CRIT X"
 *  - HeroProjection     : KPIs équipe MTD (active, sessions, hours, valid days)
 *  - EtatBlock          : 2 onglets Today + Month, breakdown par serveur
 *  - TopActionsSection  : créateurs nécessitant une action (onboarding/inactive)
 *
 * Route : `/manager/dashboard` (bascule depuis V1) ET `/manager/v2/today`.
 * Protégée par `<FeatureGuard feature="pages.manager.today">` côté Routes.jsx.
 *
 * R3 : mobile-first (Sidebar hidden md+, cards stacked sur mobile).
 */
const ManagerV2Today = () => {
  const { t } = useTranslation();
  const { userProfile } = useAuth();
  const { tenantName } = useTenant();
  const [collapsed, setCollapsed] = useState(false);
  const { hero, today, month, actions, criticalCount, loading, error, reload } = useManagerTodayData();

  return (
    <div className="flex min-h-screen bg-background text-foreground">
      <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} userEmail={userProfile?.email} />

      <main className="flex-1 min-w-0 overflow-x-hidden">
        {/* Header */}
        <header className="border-b border-border bg-card sticky top-0 z-10">
          <div className="px-4 md:px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground">
                {t('managerV2.today.title', 'Today')}
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                {tenantName} · {userProfile?.full_name || userProfile?.email}
              </p>
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="p-4 md:p-6 space-y-4 md:space-y-6">
          <CockpitBar
            totalCreators={hero.totalCreators}
            activeCreators={hero.activeCreators}
            critical={criticalCount}
          />

          {error && (
            <div className="rounded-2xl bg-error/5 border border-error/30 p-4 text-sm text-error flex items-center justify-between">
              <span>{t('managerV2.errorLoading', 'Failed to load data.')} {String(error.message || error)}</span>
              <button
                type="button"
                onClick={reload}
                className="px-3 py-1.5 rounded-lg border border-error/40 hover:bg-error/10 text-xs font-medium"
              >
                {t('common.retry', 'Retry')}
              </button>
            </div>
          )}

          <HeroProjection {...hero} loading={loading} />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            <EtatBlock
              byServer={month.byServerMonth}
              topToday={today.topToday}
              loading={loading}
            />
            <TopActionsSection
              actions={actions}
              criticalCount={criticalCount}
              loading={loading}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default ManagerV2Today;
